/**
 * DEC3: the workspace's Result-definition history and the Awareness metric in
 * force, for Settings. A failed read is 'failed' — never "no mapping", which
 * would silently mean Impressions.
 *
 * @module hooks/useResultMappings
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchResultMappings } from '@/services/targets/targetsService';
import { currentAwareness, type AwarenessMetric, type ResultMappingRow } from '@/services/targets/resultMetric';
import { logError } from '@/services/logger';

export type ResultMappingsState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; rows: ResultMappingRow[]; awareness: AwarenessMetric };

export const useResultMappings = (workspaceId: string | null): { state: ResultMappingsState; reload: () => void } => {
  const [state, setState] = useState<ResultMappingsState>({ status: 'loading' });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    if (!workspaceId) return;
    let ignore = false;
    fetchResultMappings(workspaceId)
      .then((rows) => ({ rows, awareness: currentAwareness(rows) }))
      .then(
        (ready) => { if (!ignore) setState({ status: 'ready', ...ready }); },
        (err: unknown) => {
          logError('useResultMappings', 'result mapping read failed', err);
          if (!ignore) setState({ status: 'failed' });
        },
      );
    return () => { ignore = true; };
  }, [workspaceId, tick]);
  return { state, reload };
};
